import type { ComponentRowData } from "~~/types/shipexkurs";

/**
 * Fasst strukturell identische Zeilen eines Component-Baums zusammen — nicht nur
 * auf einer Ebene (z.B. mehrere Raketen an einem Träger, mehrere Waffenhalterungen
 * an einem Turm), sondern rekursiv über den ganzen Baum: ein Turm mit mehreren
 * baugleichen Halterungen zeigt am Ende genau eine Halterungs-Zeile mit `count`,
 * deren eigene Kinder (die Waffe) ebenfalls mit dem korrekten Gesamt-`count`
 * versehen sind (Anzahl Halterungen × Anzahl Waffen je Halterung).
 *
 * Merge-Kriterium ist der bereits (namenlose) Dedup-`key` der Zeile *plus* die
 * exakte Zusammensetzung ihrer (schon gemergten) Kinder — zwei Raketenträger mit
 * unterschiedlicher Munitionsbestückung dürfen nicht zusammenfallen, auch wenn
 * der Träger selbst identisch ist.
 */
export function mergeComponentRows(rows: ComponentRowData[]): ComponentRowData[] {
  // Bottom-up: erst die Kinder jeder Zeile für sich zusammenfassen.
  const normalized = rows.map((row) => ({
    ...row,
    children: mergeComponentRows(row.children),
  }));

  const groups = new Map<string, { names: Set<string>; sample: ComponentRowData; count: number }>();
  for (const row of normalized) {
    const shapeKey = `${row.key}::${row.children.map((child) => `${child.key}x${child.count}`).join(",")}`;
    const existing = groups.get(shapeKey);
    if (existing) {
      existing.names.add(row.name);
      existing.count += row.count;
    } else {
      groups.set(shapeKey, { names: new Set([row.name]), sample: row, count: row.count });
    }
  }

  return [...groups.entries()].map(([shapeKey, { names, sample, count }]) => ({
    ...sample,
    // Zwei Gruppen können denselben `row.key` aber unterschiedliche Kinder
    // haben (z.B. Raketenträger mit unterschiedlicher Munition) — der
    // `shapeKey` bleibt dann eindeutig und dient zugleich als Vue-`:key`.
    key: shapeKey,
    name: [...names].join(", "),
    count,
    // Jede gemergte Instanz bringt ihre eigene Kopie der Kinder mit — deren
    // `count` muss also mit der Anzahl gemergter Instanzen skaliert werden,
    // nicht nur der einer einzelnen Beispiel-Instanz entsprechen.
    children: count > 1 ? scaleComponentRows(sample.children, count) : sample.children,
  }));
}

function scaleComponentRows(rows: ComponentRowData[], factor: number): ComponentRowData[] {
  return rows.map((row) => ({
    ...row,
    count: row.count * factor,
    children: scaleComponentRows(row.children, factor),
  }));
}
